export type SmsEncoding = 'GSM-7' | 'UTF-16';

export interface SmsSegmentEvaluation {
    encoding: SmsEncoding;
    parts: number;
    overLimit: boolean;
    canSave: boolean;
    hasEmoji: boolean;
    segmentFills: [number, number, number, number];
    messages: string[];
    warnings: string[];
}

export const SMS_SHORTLINK_LENGTH = 14;
export const SMS_OPT_OUT_TEXT = '\nReply STOP to unsubscribe. Msg & data rates may apply.';
export const SMS_MAX_SEGMENTS = 3;

const GSM_CHARS = new Set(
    [
        ...'0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz\n\r !"#$%&\'()*+,-./:;<=>?@[\\]^_{}|~£¥èéùìòØøÅåÆæßÉ¡ÄÖÑÜ¿äöñüà'
    ]
);
const GSM_ESCAPED = new Set([...'~^|{}[]']);
const SHORTLINK_PLACEHOLDER = 'x'.repeat(SMS_SHORTLINK_LENGTH);
const URL_REGEX = /(https?:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9-]+\.[a-zA-Z]{2,}[^\s]*)/g;

export function extractSmsUrls(text: string): string[] {
    URL_REGEX.lastIndex = 0;
    const matches = text.match(URL_REGEX) || [];
    return matches.map(url => url.replace(/[.,!?;:]+$/, ''));
}

function replaceUrlsForCounting(text: string): string {
    const urls = [...new Set(extractSmsUrls(text))].sort((a, b) => b.length - a.length);
    let result = text;
    for (let i = 0; i < urls.length; i++) {
        const url = urls[i];
        if (!url) {
            continue;
        }
        result = result.split(url).join(SHORTLINK_PLACEHOLDER);
    }
    return result;
}

function telnyxParts(text: string): { encoding: SmsEncoding; weight: number; parts: number } {
    const chars = Array.from(text);
    const gsm7 = chars.every(c => GSM_CHARS.has(c));

    if (gsm7) {
        const weight = chars.reduce((n, c) => n + (GSM_ESCAPED.has(c) ? 2 : 1), 0);
        return {
            encoding: 'GSM-7',
            weight,
            parts: weight <= 160 ? 1 : Math.ceil(weight / 153)
        };
    }

    const weight = text.length;
    return {
        encoding: 'UTF-16',
        weight,
        parts: weight <= 70 ? 1 : Math.ceil(weight / 67)
    };
}

function containsEmoji(text: string): boolean {
    for (let i = 0; i < text.length; ) {
        const codePoint = text.codePointAt(i) as number;
        if (
            codePoint > 0xffff ||
            (codePoint >= 0x2600 && codePoint <= 0x27bf) ||
            (codePoint >= 0xfe00 && codePoint <= 0xfe0f) ||
            codePoint === 0x200d
        ) {
            return true;
        }
        i += codePoint > 0xffff ? 2 : 1;
    }
    return false;
}

function clamp01(value: number): number {
    return Math.min(1, Math.max(0, value));
}

function segmentFills(weight: number, encoding: SmsEncoding): [number, number, number, number] {
    const singleLimit = encoding === 'GSM-7' ? 160 : 70;
    const concatSize = encoding === 'GSM-7' ? 153 : 67;
    const twoMax = concatSize * 2;
    const threeMax = concatSize * 3;
    const fourMax = concatSize * 4;

    return [
        clamp01(weight / singleLimit),
        weight <= singleLimit ? 0 : clamp01((weight - singleLimit) / (twoMax - singleLimit)),
        weight <= twoMax ? 0 : clamp01((weight - twoMax) / (threeMax - twoMax)),
        weight <= threeMax ? 0 : clamp01((weight - threeMax) / (fourMax - threeMax))
    ];
}

export function evaluateSmsSegments(text?: string | null): SmsSegmentEvaluation {
    const body = replaceUrlsForCounting(text || '');
    const { encoding, weight, parts } = telnyxParts(body + SMS_OPT_OUT_TEXT);
    const hasEmoji = containsEmoji(body);
    const overLimit = parts > SMS_MAX_SEGMENTS;
    const messages: string[] = [];
    const warnings: string[] = [];

    if (overLimit) {
        messages.push('This message is too long and cannot be saved in its current form.');
        if (hasEmoji) {
            messages.push('Removing the emojis will double the number of characters you can use.');
        }
    } else if (parts === SMS_MAX_SEGMENTS) {
        warnings.push('Long messages have a higher risk of being blocked by cell provider spam detection.');
    }

    return {
        encoding,
        parts,
        overLimit,
        canSave: !overLimit,
        hasEmoji,
        segmentFills: segmentFills(weight, encoding),
        messages,
        warnings
    };
}
